<template>
  <div :class="['min-h-screen flex items-center justify-center py-8 px-4 bg-gray-50 relative', { dark: isDark }]" :style="bgStyle">
    <!-- Theme toggle floating in the corner — visitors can override the
         host's default light/dark setting just for their session. -->
    <button v-if="link" type="button" @click="toggleTheme"
      :title="isDark ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'"
      :aria-label="isDark ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'"
      class="absolute top-4 right-4 z-10 size-10 inline-flex items-center justify-center rounded-full
             bg-white/85 dark:bg-gray-900/70 backdrop-blur ring-1 ring-black/10 dark:ring-white/10
             text-gray-700 dark:text-gray-200 shadow-sm transition hover:scale-105 active:scale-95">
      <span class="material-symbols-outlined text-[20px]">{{ isDark ? 'light_mode' : 'dark_mode' }}</span>
    </button>

    <div class="w-full max-w-3xl">

      <!-- Loading -->
      <div v-if="loading" class="flex items-center justify-center py-20">
        <div class="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin"></div>
      </div>

      <!-- Not found -->
      <div v-else-if="error" class="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-10 text-center">
        <span class="material-symbols-outlined text-6xl text-gray-300 dark:text-gray-600">search_off</span>
        <h1 class="mt-4 text-2xl font-bold text-gray-900 dark:text-white">Enlace no disponible</h1>
        <p class="mt-2 text-gray-500 dark:text-gray-400">{{ error }}</p>
      </div>

      <!-- Confirmation screen -->
      <div v-else-if="confirmed" class="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-10 text-center">
        <span class="material-symbols-outlined text-6xl text-green-500">check_circle</span>
        <h1 class="mt-4 text-2xl font-bold text-gray-900 dark:text-white">¡Listo, agendaste tu {{ link.kind === 'meeting' ? 'reunión' : 'cita' }}!</h1>
        <p class="mt-2 text-gray-500 dark:text-gray-400">
          {{ formatLongDate(confirmed.starts_at) }}
        </p>
        <p class="mt-1 text-sm text-gray-400 dark:text-gray-500">
          Te enviamos los detalles a tu correo.
        </p>
        <p v-if="confirmed.confirmation_message" class="mt-6 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
          {{ confirmed.confirmation_message }}
        </p>
      </div>

      <!-- Main flow -->
      <div v-else class="bg-white dark:bg-gray-900 rounded-2xl shadow-lg overflow-hidden">
        <!-- Header -->
        <div class="p-6 border-b border-gray-100 dark:border-white/10">
          <div class="flex items-center gap-3">
            <p class="text-xs font-bold uppercase tracking-wider flex-1 text-gray-500 dark:text-gray-400">
              {{ link.kind === 'meeting' ? 'Reunión' : 'Evento' }} · {{ link.duration_minutes }} min
            </p>
            <img v-if="displayedLogoUrl" :src="displayedLogoUrl"
              :alt="link?.host?.company_name || link?.host?.name || 'Logo'"
              @error="onLogoError"
              class="size-12 rounded-full object-cover bg-white dark:bg-gray-800 ring-2 ring-gray-100 dark:ring-white/10 shadow-sm shrink-0" />
          </div>
          <h1 class="mt-2 text-2xl font-black text-gray-900 dark:text-white">{{ link.title }}</h1>
          <p v-if="link.host?.name" class="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Con <span class="font-semibold">{{ link.host.name }}</span><span v-if="link.host.company_name"> · {{ link.host.company_name }}</span>
          </p>
          <p v-if="link.description" class="mt-3 text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap">
            {{ link.description }}
          </p>
          <div class="mt-4 flex flex-wrap items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
            <span class="flex items-center gap-1">
              <span class="material-symbols-outlined text-sm">schedule</span>
              {{ link.duration_minutes }} minutos
            </span>
            <span v-if="link.location_type === 'online'" class="flex items-center gap-1">
              <span class="material-symbols-outlined text-sm">videocam</span>
              Llamada en línea
            </span>
            <span v-else-if="link.location_type === 'phone'" class="flex items-center gap-1">
              <span class="material-symbols-outlined text-sm">call</span>
              Llamada
            </span>
            <span v-else-if="link.location_type === 'in_person'" class="flex items-center gap-1">
              <span class="material-symbols-outlined text-sm">location_on</span>
              {{ link.location_value || 'Presencial' }}
            </span>
          </div>
        </div>

        <!-- Step 1: pick date + time (Calendly-style: calendar first, then times) -->
        <div v-if="step === 'slot'" class="p-4 sm:p-6 overflow-hidden">
          <Transition :name="slotTransitionName" mode="out-in">
          <!-- Calendar view (no day selected yet) -->
          <div v-if="!selectedDate" key="calendar" class="max-w-md mx-auto">
            <div class="flex items-center justify-between mb-4">
              <button type="button" @click="prevMonth"
                class="size-9 inline-flex items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-white/5 text-gray-600 dark:text-gray-300">
                <span class="material-symbols-outlined text-[20px]">chevron_left</span>
              </button>
              <p class="text-base font-bold text-gray-900 dark:text-white">{{ monthLabel }}</p>
              <button type="button" @click="nextMonth"
                class="size-9 inline-flex items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-white/5 text-gray-600 dark:text-gray-300">
                <span class="material-symbols-outlined text-[20px]">chevron_right</span>
              </button>
            </div>
            <div class="grid grid-cols-7 text-center text-[11px] font-bold text-gray-400 dark:text-gray-500 mb-2">
              <span v-for="d in DAY_LETTERS" :key="d">{{ d }}</span>
            </div>
            <div class="grid grid-cols-7 gap-1">
              <button v-for="(d, i) in monthCells" :key="i"
                :disabled="!d.available || d.past"
                @click="selectDay(d.dateStr)"
                :class="['h-10 sm:h-11 rounded-full text-sm transition',
                  !d.inMonth ? 'opacity-30' : '',
                  d.past ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed' : '',
                  !d.available && d.inMonth && !d.past ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed' : '',
                  d.available ? 'bg-primary/10 hover:bg-primary text-primary hover:text-white font-bold' : ''
                ]">
                {{ d.day }}
              </button>
            </div>
            <div v-if="loadingSlots" class="mt-4 text-center">
              <div class="inline-block h-5 w-5 rounded-full border-2 border-primary border-t-transparent animate-spin"></div>
            </div>
          </div>

          <!-- Times view (a day is selected) -->
          <div v-else key="times">
            <button type="button" @click="backToCalendar"
              class="inline-flex items-center gap-1 text-sm font-semibold text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white">
              <span class="material-symbols-outlined text-[18px]">arrow_back</span>
              Volver al calendario
            </button>
            <p class="mt-3 text-base font-bold text-gray-900 dark:text-white capitalize">
              {{ formatLongDate(selectedDate) }}
            </p>
            <p class="mt-1 text-xs text-gray-500 dark:text-gray-400 mb-4">
              Selecciona un horario disponible
            </p>
            <div v-if="(slots[selectedDate] || []).length"
              class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 max-h-[480px] overflow-y-auto pr-1">
              <button v-for="(t, idx) in slots[selectedDate]" :key="t" type="button"
                @click="onPickTime(t)"
                :style="{ animationDelay: (idx * 28) + 'ms' }"
                class="slot-button h-11 rounded-lg border border-gray-300 dark:border-white/15 text-sm font-semibold text-gray-700 dark:text-gray-200 hover:border-primary hover:bg-primary hover:text-white transition-colors">
                {{ formatTime12h(t) }}
              </button>
            </div>
            <div v-else class="text-center py-10">
              <span class="material-symbols-outlined text-4xl text-gray-300 dark:text-gray-600">event_busy</span>
              <p class="mt-2 text-sm text-gray-400 dark:text-gray-500">No hay horarios disponibles este día.</p>
            </div>
          </div>
          </Transition>
        </div>

        <!-- Step 2: form -->
        <form v-else-if="step === 'form'" @submit.prevent="submit" class="p-6 space-y-4">
          <button type="button" @click="step = 'slot'" class="text-xs text-gray-500 dark:text-gray-400 hover:underline">
            ← Cambiar horario
          </button>
          <div class="bg-gray-50 dark:bg-white/5 rounded-lg p-3">
            <p class="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">Horario</p>
            <p class="text-sm font-bold text-gray-900 dark:text-white">{{ formatLongDate(selectedDate) }} · {{ formatTime12h(selectedTime) }}</p>
          </div>

          <div>
            <label class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Nombre completo *</label>
            <input v-model="form.name" required
              class="mt-1 w-full h-10 px-3 rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 text-sm text-gray-900 dark:text-white" />
          </div>
          <div>
            <label class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Correo *</label>
            <input type="email" v-model="form.email" required
              class="mt-1 w-full h-10 px-3 rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 text-sm text-gray-900 dark:text-white" />
          </div>

          <div v-for="f in link.custom_fields" :key="f.key">
            <label class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
              {{ f.label }}<span v-if="f.required"> *</span>
            </label>
            <input v-if="f.field_type === 'text'" v-model="form.responses[f.key]" :required="f.required"
              class="mt-1 w-full h-10 px-3 rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 text-sm text-gray-900 dark:text-white" />
            <input v-else-if="f.field_type === 'email'" type="email" v-model="form.responses[f.key]" :required="f.required"
              class="mt-1 w-full h-10 px-3 rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 text-sm text-gray-900 dark:text-white" />
            <textarea v-else-if="f.field_type === 'textarea'" v-model="form.responses[f.key]" :required="f.required" rows="3"
              class="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 text-sm text-gray-900 dark:text-white"></textarea>
            <label v-else-if="f.field_type === 'checkbox'" class="mt-1 flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input type="checkbox" v-model="form.responses[f.key]" :required="f.required" />
              {{ f.placeholder || 'Acepto' }}
            </label>
          </div>

          <p v-if="submitError" class="text-sm text-red-600 dark:text-red-400">{{ submitError }}</p>

          <button type="submit" :disabled="submitting"
            class="w-full h-11 rounded-lg bg-primary text-white text-sm font-bold hover:bg-primary/90 disabled:opacity-50">
            {{ submitting ? 'Agendando…' : 'Confirmar reserva' }}
          </button>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
// Week starts on Monday (es-MX). Index 0 = Monday … 6 = Sunday.
const DAY_LETTERS = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

function pad(n) { return String(n).padStart(2, '0'); }
function toDateStr(d) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }
function parseDateStr(s) { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); }

export default {
  name: 'BookLanding',
  inject: ['$axios'],
  props: {
    userSlug: { type: String, required: true },
    linkSlug: { type: String, required: true }
  },
  data() {
    return {
      DAY_LETTERS,
      loading: true,
      error: '',
      link: null,
      anchor: new Date(),
      slots: {},
      loadingSlots: false,
      selectedDate: null,
      selectedTime: null,
      step: 'slot',
      form: { name: '', email: '', responses: {} },
      submitting: false,
      submitError: '',
      confirmed: null,
      logoFailed: false,
      avatarFailed: false,
      utm: {},
      slotTransitionName: 'slot-forward',
      // Visitor-toggled theme. Initialized from the host's default in
      // mounted() once the link payload arrives.
      activeTheme: null
    };
  },
  computed: {
    apiPath() { return `${this.userSlug}/${this.linkSlug}`; },

    monthLabel() {
      const months = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
      return `${months[this.anchor.getMonth()]} ${this.anchor.getFullYear()}`;
    },
    displayedLogoUrl() {
      const host = this.link?.host;
      const assets_host = this.link?.assets_host;

      if (!host) return null;
      // Try company logo first; if broken, fall back to host avatar; if both
      // are missing or broken, show nothing.
      if (host.logo_url && !this.logoFailed) return assets_host + host.logo_url;
      if (host.avatar_url && !this.avatarFailed) return assets_host + host.avatar_url;
      return null;
    },
    // The host's branding payload, with backward-compat for the old flat
    // shape (legacy data without nested light/dark objects).
    branding() {
      const b = this.link?.host?.branding;
      if (!b) return null;
      const legacyFlat = !b.light && !b.dark && (b.bg_mode || b.bg_color_from);
      return {
        theme: b.theme === 'dark' ? 'dark' : 'light',
        light: b.light || (legacyFlat ? b : { bg_mode: 'solid', bg_color_from: '#f9fafb', bg_color_to: '#e5e7eb', bg_angle: 135 }),
        dark:  b.dark  || { bg_mode: 'solid', bg_color_from: '#0f172a', bg_color_to: '#1e293b', bg_angle: 135 }
      };
    },
    effectiveTheme() {
      // Visitor toggle overrides everything. Otherwise the landing picks
      // dark between 7pm and 7am (local time of the visitor) and light
      // during the day — like the dashboard's "schedule" theme. The
      // host's stored `branding.theme` is intentionally ignored here so
      // someone hitting the link at night never gets blasted with a
      // light background.
      if (this.activeTheme) return this.activeTheme;
      const h = new Date().getHours();
      return (h >= 19 || h < 7) ? 'dark' : 'light';
    },
    isDark() { return this.effectiveTheme === 'dark'; },
    activePalette() {
      return this.branding ? this.branding[this.effectiveTheme] : null;
    },
    bgStyle() {
      const p = this.activePalette;
      if (!p || !p.bg_mode) return null;
      const from  = p.bg_color_from || '#f9fafb';
      const to    = p.bg_color_to   || '#e5e7eb';
      const angle = Number.isFinite(Number(p.bg_angle)) ? Number(p.bg_angle) : 135;
      if (p.bg_mode === 'linear') {
        return { background: `linear-gradient(${angle}deg, ${from}, ${to})` };
      }
      if (p.bg_mode === 'radial') {
        return { background: `radial-gradient(circle at center, ${from}, ${to})` };
      }
      return { background: from };
    },
    monthCells() {
      const d = this.anchor;
      const first = new Date(d.getFullYear(), d.getMonth(), 1);
      // Monday-first: Mon=0 … Sun=6.
      const offset = (first.getDay() + 6) % 7;
      const start = new Date(first);
      start.setDate(1 - offset);
      const today = new Date(); today.setHours(0,0,0,0);

      const cells = [];
      for (let i = 0; i < 42; i++) {
        const cur = new Date(start);
        cur.setDate(start.getDate() + i);
        const dateStr = toDateStr(cur);
        cells.push({
          day: cur.getDate(),
          dateStr,
          inMonth: cur.getMonth() === d.getMonth(),
          past: cur < today,
          available: (this.slots[dateStr] || []).length > 0
        });
      }
      return cells;
    }
  },
  mounted() {
    this.captureUtm();
    this.fetchLink();
  },
  methods: {
    toggleTheme() {
      this.activeTheme = this.isDark ? 'light' : 'dark';
    },
    selectDay(date) {
      this.slotTransitionName = 'slot-forward';
      this.selectedDate = date;
    },
    backToCalendar() {
      this.slotTransitionName = 'slot-back';
      this.selectedDate = null;
    },
    captureUtm() {
      // UTM attribution: any utm_* param on the public URL flows into the
      // booking submission so the resulting Lead remembers its origin.
      try {
        const params = new URLSearchParams(window.location.search);
        const keys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
        const utm = {};
        keys.forEach(k => {
          const v = params.get(k);
          if (v) utm[k] = v;
        });
        this.utm = utm;
      } catch (_) {
        this.utm = {};
      }
    },
    onLogoError() {
      const host = this.link?.host;
      // First failure: assume the company logo URL is broken — try the
      // host avatar next. Second failure: give up so we don't loop.
      if (host?.logo_url && !this.logoFailed) {
        this.logoFailed = true;
        return;
      }
      this.avatarFailed = true;
    },
    formatLongDate(s) {
      if (!s) return '';
      const d = typeof s === 'string' && s.length === 10 ? parseDateStr(s) : new Date(s);
      return d.toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    },
    formatTime12h(t) {
      if (!t) return '';
      const [hStr, mStr] = t.split(':');
      const h = Number(hStr);
      const m = Number(mStr);
      if (Number.isNaN(h) || Number.isNaN(m)) return t;
      const period = h >= 12 ? 'p.m.' : 'a.m.';
      const h12 = h % 12 === 0 ? 12 : h % 12;
      return `${h12}:${String(m).padStart(2, '0')} ${period}`;
    },
    async fetchLink() {
      this.loading = true;
      try {
        const { data } = await this.$axios.get(this.apiPath);
        this.link = data.data;
        // Initialize responses scaffold
        (this.link.custom_fields || []).forEach(f => {
          this.form.responses[f.key] = f.field_type === 'checkbox' ? false : '';
        });
        await this.fetchSlots();
      } catch (e) {
        this.error = e.response?.data?.error || 'No encontramos esta liga.';
      } finally {
        this.loading = false;
      }
    },
    async fetchSlots() {
      this.loadingSlots = true;
      try {
        const first = new Date(this.anchor.getFullYear(), this.anchor.getMonth(), 1);
        const last  = new Date(this.anchor.getFullYear(), this.anchor.getMonth() + 1, 0);
        const { data } = await this.$axios.get(`${this.apiPath}/availability`, {
          params: { from: toDateStr(first), to: toDateStr(last) }
        });
        this.slots = data.data || {};
      } finally {
        this.loadingSlots = false;
      }
    },
    prevMonth() {
      const d = new Date(this.anchor);
      d.setMonth(d.getMonth() - 1);
      this.anchor = d;
      this.selectedDate = null;
      this.fetchSlots();
    },
    nextMonth() {
      const d = new Date(this.anchor);
      d.setMonth(d.getMonth() + 1);
      this.anchor = d;
      this.selectedDate = null;
      this.fetchSlots();
    },
    onPickTime(t) {
      this.selectedTime = t;
      this.step = 'form';
    },
    buildStartsAt() {
      const [y, m, d] = this.selectedDate.split('-').map(Number);
      const [h, min] = this.selectedTime.split(':').map(Number);
      const dt = new Date(y, m - 1, d, h, min, 0);
      return dt.toISOString();
    },
    async submit() {
      this.submitting = true;
      this.submitError = '';
      try {
        const payload = {
          name:       this.form.name,
          email:      this.form.email,
          starts_at:  this.buildStartsAt(),
          responses:  this.form.responses,
          utm:        this.utm
        };
        const { data } = await this.$axios.post(`${this.apiPath}/bookings`, payload);
        this.confirmed = data.data;
      } catch (e) {
        this.submitError = e.response?.data?.error || 'No se pudo agendar';
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>

<style scoped>
/* Calendar ↔ times slide transitions. `mode="out-in"` already guarantees
   the old view is gone before the new one mounts, so we just need
   enter/leave keyframes that match direction. */
.slot-forward-enter-active,
.slot-back-enter-active {
  transition: opacity 240ms ease, transform 280ms cubic-bezier(0.22, 0.61, 0.36, 1);
}
.slot-forward-leave-active,
.slot-back-leave-active {
  transition: opacity 180ms ease, transform 200ms cubic-bezier(0.55, 0.06, 0.68, 0.19);
}
.slot-forward-enter-from {
  opacity: 0;
  transform: translateX(32px);
}
.slot-forward-leave-to {
  opacity: 0;
  transform: translateX(-32px);
}
.slot-back-enter-from {
  opacity: 0;
  transform: translateX(-32px);
}
.slot-back-leave-to {
  opacity: 0;
  transform: translateX(32px);
}

/* Staggered fade-up for individual time-slot buttons. The inline
   `animation-delay` set in the template walks through them. */
.slot-button {
  animation: slot-rise 260ms cubic-bezier(0.22, 0.61, 0.36, 1) both;
}
@keyframes slot-rise {
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
}
</style>
